import React, { useEffect, useState, useCallback } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { EntryForm } from "@/components/EntryForm";
import { EntriesTable } from "@/components/EntriesTable";
import { DowntimeForm } from "@/components/DowntimeForm";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function ProductionEntry() {
  const { user } = useAuth();
  const [meta, setMeta] = useState({ departments: [], machines: [], employees: [], teams: [], products: [], operations: [], shift_definitions: [] });
  const [entries, setEntries] = useState([]);
  const [sel, setSel] = useState([]);
  const [listRange, setListRange] = useState({ start: "", end: "" });
  const [rangeInput, setRangeInput] = useState({ start: "", end: "" });

  const load = useCallback(() => {
    api.get("/meta/options").then((r) => setMeta(r.data)).catch(() => {});
    const q = listRange.start && listRange.end
      ? `start=${listRange.start}&end=${listRange.end}&limit=2000`
      : "limit=300";
    api.get(`/entries?${q}`).then((r) => setEntries(r.data)).catch(() => {});
  }, [listRange]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Kayıt silinsin mi?")) return;
    try { await api.delete(`/entries/${id}`); toast.success("Kayıt silindi"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const toggleSel = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleAll = () => setSel((s) => s.length === entries.length ? [] : entries.map((e) => e.id));

  const removeSelected = async () => {
    if (!sel.length) { toast.error("Silinecek kayıt seçilmedi"); return; }
    if (!window.confirm(`Seçili ${sel.length} üretim kaydı silinsin mi?`)) return;
    try {
      const r = await api.post("/entries/bulk-delete", { ids: sel });
      toast.success(`${r.data.deleted} kayıt silindi`);
      setSel([]); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const removeAll = async () => {
    if (!window.confirm("TÜM üretim kayıtları kalıcı olarak silinecek. Emin misiniz?")) return;
    if (!window.confirm("Bu işlem geri alınamaz! Devam edilsin mi?")) return;
    try {
      const r = await api.delete("/entries");
      toast.success(`Tüm kayıtlar silindi (${r.data.deleted} kayıt)`);
      setSel([]); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="production-entry-page" className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Günlük Üretim Girişi</h1>
        <p className="text-sm text-muted-foreground mt-1">Makine, manuel ve ekip bazlı üretim kayıtları</p>
      </div>
      <Card className="p-5 bg-card border-border">
        <Tabs defaultValue="machine">
          <TabsList className="bg-secondary" data-testid="entry-type-tabs">
            <TabsTrigger value="machine" data-testid="tab-entry-machine">Makine Üretimi</TabsTrigger>
            <TabsTrigger value="manual" data-testid="tab-entry-manual">Manuel Üretim</TabsTrigger>
            <TabsTrigger value="team" data-testid="tab-entry-team">Ekip Üretimi</TabsTrigger>
            <TabsTrigger value="downtime" data-testid="tab-entry-downtime">Duruş Kaydı</TabsTrigger>
          </TabsList>
          <TabsContent value="machine" className="mt-4"><EntryForm type="machine" meta={meta} onSaved={load} /></TabsContent>
          <TabsContent value="manual" className="mt-4"><EntryForm type="manual" meta={meta} onSaved={load} /></TabsContent>
          <TabsContent value="team" className="mt-4"><EntryForm type="team" meta={meta} onSaved={load} /></TabsContent>
          <TabsContent value="downtime" className="mt-4"><DowntimeForm meta={meta} /></TabsContent>
        </Tabs>
      </Card>

      <EntriesTable entries={entries} sel={sel} meta={meta} isAdmin={user?.role === "admin"}
        rangeInput={rangeInput} setRangeInput={setRangeInput} isFiltered={!!listRange.start}
        onApplyFilter={() => setListRange(rangeInput)}
        onClearFilter={() => { setRangeInput({ start: "", end: "" }); setListRange({ start: "", end: "" }); }}
        onToggle={toggleSel} onToggleAll={toggleAll}
        onRemove={remove} onRemoveSelected={removeSelected} onRemoveAll={removeAll} />
    </div>
  );
}
